import React, { useEffect, useState } from "react";
import { useStoreActions, useStoreState } from "../../store";
import { useAuth } from "../../utility/useAuth";
import { PartOrderWithImages } from "../../types/PartOrderWithImages";
import { PathItem } from "../../types/PathItem";
import { getDocumentContent, getDocuments } from "../../services/ApiService";
import { ToastUtility } from "@syncfusion/ej2-react-notifications";
import { HeaderMode } from "../../types/HeaderMode";
import {
  MenuComponent,
  MenuEventArgs,
  MenuItemModel,
} from "@syncfusion/ej2-react-navigations";
import {
  Inject,
  ListBoxChangeEventArgs,
  ListBoxComponent,
} from "@syncfusion/ej2-react-dropdowns";
import {
  ColumnDirective,
  ColumnsDirective,
  GridComponent,
  Resize,
  RowSelectEventArgs,
  SelectionSettingsModel,
  Sort,
} from "@syncfusion/ej2-react-grids";
import { getPathItem } from "../../utility/pathHelper";
import { Col, Container, Row } from "react-grid-system";
import { StyledSimilarJobsImagesPage } from "./SimilarJobsImages.styles";
import { ViewImageModal } from "../../components/Modals/ViewImageModal/ViewImageModal";
import { useNavigate } from "react-router";

export const SimilarJobsImagesPage: React.FC = () => {
  let menuObj: MenuComponent | null;
  let listboxObj: ListBoxComponent | null;
  const { similarJobs } = useStoreState((state) => state.appSettings);

  const { setHeaderMode } = useStoreActions((actions) => actions.appSettings);
  const auth = useAuth();
  const navigate = useNavigate();

  const [partsOrders, setPartOrders] = useState<PartOrderWithImages[]>([]);
  const [selectedPartsOrder, setSelectedPartOrder] =
    useState<PartOrderWithImages>();
  const [selectedImages, setSelectedImages] = useState<PathItem[]>([]);
  const [selectedImage, setSelectedImage] = useState<PathItem>();
  const [showViewImageModal, setShowViewImageModal] = useState(false);

  async function loadPartOrders(): Promise<PartOrderWithImages[]> {
    if (!similarJobs) {
      return [];
    }

    const documents = await getDocuments(auth, "images");

    let orders: PartOrderWithImages[] = [];

    similarJobs.forEach((jobDetails) => {
      for (let i = 1; i <= jobDetails.qtyOrdered; i++) {
        const weld = `${jobDetails.job}-${i}`;
        const partOrderImages = documents.filter((d) =>
          d.startsWith(`images${weld}`)
        );

        orders.push({
          weldId: weld,
          stockCode: jobDetails.stockCode,
          imageCount: partOrderImages.length,
          images: partOrderImages,
        });
      }
    });

    setPartOrders(orders);
    return orders;
  }

  useEffect(() => {
    const fetchData = async () => {
      // get the data from the api
      await loadPartOrders();
    };

    setHeaderMode(HeaderMode.SimilarProductionOrdersImages);
    if (similarJobs) {
      fetchData()
        // make sure to catch any error
        .catch((error) => {
          const message = `Failed to get images. Error: ${error}`;
          console.error(`Failed to get job details. Error: ${error}`);
          ToastUtility.show(message, "Error", 2000);
        });
    }
  }, [similarJobs]);

  const menuItems: MenuItemModel[] = [
    { id: "back", text: "Back", iconCss: "e-icons e-back" },
    {
      id: "downloadImage",
      text: "Download",
      iconCss: "e-icons e-align-bottom",
    },
  ];

  const toolTips = new Map<string, string>([
    ["downloadImage", "Download Image"],
  ]);

  function beforeItemRender(args: MenuEventArgs): void {
    args.element.setAttribute("title", toolTips.get(args.item.id ?? "") ?? "");
  }

  function created(): void {
    // Disable items
    if (menuObj) {
      menuObj.enableItems(["Download"], false, false);
    }
  }

  function selectMenuItem(args: MenuEventArgs): void {
    const id = args.item.id;

    if (id === "downloadImage" && selectedImage) {
      getDocumentContent(auth, selectedImage.folder, selectedImage.fileName)
        .then((data) => {
          if (data) {
            const href = URL.createObjectURL(data);
            const link = document.createElement("a");
            link.href = href;
            link.setAttribute("download", selectedImage.fileName); //or any other extension
            document.body.appendChild(link);
            link.click();

            // clean up "a" element & remove ObjectURL
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
          }
        })
        .catch((error) => {
          const message = `Failed to get image content. Error: ${error}`;
          console.error(message);
          //ToastUtility.show(message, "Error", 2000);
          return;
        });
    } else if (id === "back") {
      navigate(-1);
    }
  }

  function onPartsOrderSelected(args: RowSelectEventArgs) {
    if (menuObj) {
      menuObj.enableItems(["Download"], false, false);
    }
    const partOrder = args.data as PartOrderWithImages;
    if (partOrder) {
      console.log(`onPartsOrderSelected: ${(args.data as PartOrderWithImages)?.weldId}`);
      //setSelectedPartOrder(partsOrders[args.rowIndex]);
      
      setSelectedPartOrder(partOrder);
      const images = partOrder?.images.map((image) =>
        getPathItem(image)
      );
      if (images) {
        setSelectedImages(images);
      }
    }
  }

  function onImageSelected(args: ListBoxChangeEventArgs) {
    if (menuObj) {
      menuObj.enableItems(["Download"], false, false);
    }
    if (args.items && args.items.length === 1) {
      setSelectedImage(args.items[0] as PathItem);
      if (menuObj) {
        menuObj.enableItems(["Download"], true, false);
      }
    }
  }

  const selectionSettings: SelectionSettingsModel = {
    mode: "Row",
    type: "Single",
  };

  function Template(data: any): JSX.Element {
    const [img, setImg] = useState<string>();

    const fetchImage = async () => {
      const res = await getDocumentContent(
        auth,
        data.folder,
        data.fileName,
        true
      ).catch((error) => {
        const message = `Failed to get image content. Error: ${error}`;
        console.error(message);
        return;
      });
      if (res) {
        const imageObjectURL = URL.createObjectURL(res);
        setImg(imageObjectURL);
      }
    };

    useEffect(() => {
      fetchImage();
    }, []);

    return (
      //<div className="list-wrapper" style={{ width: 400 }}>
      <Container
        fluid
        style={{ margin: 10 }}
        onDoubleClick={() => {
          const image = selectedImages.find(
            (i) => i.fileName === data.fileName
          );
          console.log(
            `double clicked listboxObj: ${data.fileName} ${JSON.stringify(
              image
            )}`
          );
          setSelectedImage(image);
          setShowViewImageModal(true);
        }}
      >
        <Row align="stretch">
          <span>{`${data.fileName}`}</span>
        </Row>
        <Row align="stretch">
          <img height={120} width={120} src={img} />
        </Row>
      </Container>
      //</div>
    );
  }

  return (
    <StyledSimilarJobsImagesPage>
      {partsOrders ? (
        <Container fluid>
          <Row align="stretch">
            <MenuComponent
              ref={(scope: MenuComponent) => (menuObj = scope)}
              items={menuItems}
              select={selectMenuItem}
              beforeItemRender={beforeItemRender}
              created={created}
            />
          </Row>
          <Row align="stretch">
            <Col sm={6}>
              <GridComponent
                id="Grid"
                dataSource={partsOrders}
                selectionSettings={selectionSettings}
                allowResizing
                allowSorting
                height={600}
                gridLines="Both"
                rowSelected={onPartsOrderSelected}
              >
                <Inject services={[Resize, Sort]} />
                <ColumnsDirective>
                  <ColumnDirective
                    field="weldId"
                    isPrimaryKey={true}
                    headerText="Part Production Order"
                    width="150"
                    textAlign="Left"
                  />
                  <ColumnDirective
                    field="stockCode"
                    headerText="StockCode"
                    width="150"
                    textAlign="Left"
                  />
                  <ColumnDirective
                    field="imageCount"
                    headerText="Image Count"
                    width="100"
                    textAlign="Left"
                  />
                </ColumnsDirective>
              </GridComponent>
            </Col>
            {selectedPartsOrder && (
              <Col>
                <ListBoxComponent
                  id="listbox"
                  ref={(scope: ListBoxComponent) => (listboxObj = scope)}
                  height={650}
                  dataSource={selectedImages}
                  selectionSettings={{ mode: "Single" }}
                  itemTemplate={Template}
                  change={onImageSelected}
                  fields={{ text: "fileName" }}
                />
              </Col>
            )}
          </Row>
        </Container>
      ) : (
        <>Loading...</>
      )}
      {showViewImageModal && selectedPartsOrder && selectedImage?.fileName && (
        <ViewImageModal
          open={showViewImageModal}
          partProductionOrder={selectedPartsOrder?.weldId}
          imageName={selectedImage.fileName}
          onClose={() => {
            setShowViewImageModal(false);
          }}
        />
      )}
    </StyledSimilarJobsImagesPage>
  );
};
